import { ApolloQueryResult, useApolloClient, useQuery } from "@apollo/client";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { LoginContext } from "../../../components/LoginProvider/LoginProvider";
import useCrossTabState from "../../../hooks/useCrossTabState/useCrossTabState";
import { GetEssentialUserInfo_me } from "../../../types/GetEssentialUserInfo";
import { GetStripeProviderInfo_membershipSubscription_stripeProviderData } from "../../../types/GetStripeProviderInfo";
import {
  GetSubscriptionInfo,
  GetSubscriptionInfo_membershipSubscription,
} from "../../../types/GetSubscriptionInfo";
import GetSubscriptionInfoQuery from "../../../graphql/GetSubscriptionInfo.graphql";
import GetStripeProviderInfoQuery from "../../../graphql/GetStripeProviderInfo.graphql";
import { getWelcomeURL } from "../utils/getWelcomeURL";
import { getPlanOrigin } from "../utils/getPlanOrigin";
import {
  MembershipSubscriptionProvider,
  MembershipSubscriptionStatus,
} from "../../../types/graphql-global-types";
import { getLocale } from "../../../utils/getLocale";
import { navigateToMyAccount } from "../utils/navigateToMyAccount";
import { navigateToCredentials } from "../utils/navigateToCredentials";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getLoggedInHomeURL } from "../utils/getLoggedInHomeURL";

export const PaymentContext = createContext<{
  user: GetEssentialUserInfo_me | undefined;
  selectedPlan: "ANNUAL" | "MONTHLY";
  setSelectedPlan: React.Dispatch<React.SetStateAction<"ANNUAL" | "MONTHLY">>;
  selectedMethod: MembershipSubscriptionProvider.STRIPE | MembershipSubscriptionProvider.PAYPAL;
  setSelectedMethod: React.Dispatch<React.SetStateAction<"STRIPE" | "PAYPAL">>;
  isNew: boolean;
  isReady: boolean;
  paymentError: boolean | string;
  setPaymentError: React.Dispatch<React.SetStateAction<string | boolean>>;
  paymentLoading: number | boolean;
  setPaymentLoading: React.Dispatch<React.SetStateAction<string>>;
  refetchSubscription: () => Promise<ApolloQueryResult<GetSubscriptionInfo>> | Promise<void>;
  pollMembershipStatus: (
    checkStripe?: any
  ) => Promise<boolean | GetStripeProviderInfo_membershipSubscription_stripeProviderData> | Promise<void>;
  subscription: GetSubscriptionInfo_membershipSubscription | undefined | null;
}>({
  user: undefined,
  selectedPlan: "ANNUAL",
  setSelectedPlan: () => undefined,
  selectedMethod: MembershipSubscriptionProvider.STRIPE,
  setSelectedMethod: () => undefined,
  isNew: false,
  isReady: true,
  paymentError: false,
  setPaymentError: () => undefined,
  paymentLoading: false,
  setPaymentLoading: () => undefined,
  refetchSubscription: () => Promise.resolve(),
  pollMembershipStatus: () => Promise.resolve(),
  subscription: undefined,
});

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PUBLISHABLE_KEY as string);

export const PaymentProvider: React.FunctionComponent<{ children?: React.ReactNode }> = (props) => {
  // skipGetSubscription state var skips initial execution of GetSubscriptionInfo query hook.
  // subscriptionData stores query result,
  // refetchSubscription manually refetches query.
  // Used to retrieve and update current user's subscription info.
  const [skipGetSubscription, setSkipGetSubscription] = useState<boolean>(true);
  const { data: subscriptionData, refetch: refetchSubscription } = useQuery<GetSubscriptionInfo>(
    GetSubscriptionInfoQuery,
    {
      skip: skipGetSubscription,
    }
  );

  // Shorter alias for subscription data
  const subscription = subscriptionData?.membershipSubscription;

  // User related
  const { loading: loginLoading, user: userData, loggedIn: isLoggedIn } = useContext(LoginContext);

  // Selected plan, ANNUAL or MONTHLY
  const [selectedPlan, setSelectedPlan] = useCrossTabState("selectedPlan", "ANNUAL");

  // Selected method, PAYPAL or STRIPE
  const [selectedMethod, setSelectedMethod] = useCrossTabState(
    "selectedMethod",
    subscription?.provider === MembershipSubscriptionProvider.PAYPAL
      ? MembershipSubscriptionProvider.PAYPAL
      : MembershipSubscriptionProvider.STRIPE
  );

  // Boolean if subscription status is new
  const isNew = subscription?.status === "NEW";

  // Current site language
  const locale = getLocale();

  // Handle logged out user
  useEffect(() => {
    if (!loginLoading && !userData && !isLoggedIn) {
      navigateToCredentials("account");
    }
  }, [isLoggedIn, loginLoading, userData]);

  // Used to track if there is an error with payment processing
  const [paymentError, setPaymentError] = useState<boolean | string>(false);

  // State variable shared across tabs and stored in local storage. It tracks payment processing status.
  const [paymentLoading, setPaymentLoading] = useCrossTabState("paymentLoading", "false");

  // Value of the 'plan' query parameter
  const planOrigin = getPlanOrigin();

  // Instance of the Apollo Client, which is used to make GraphQL requests
  const client = useApolloClient();

  // This function polls the membership status and redirects the user if their membership is active.
  const pollingInterval = 180;
  const pollingTries = 160;
  const pollMembershipStatus = useCallback(
    // The checkStripe parameter determines whether or not to check the status of the Stripe provider data as well.
    (checkStripe = false) => {
      let triesLeft = pollingTries;
      // The pollMembershipStatus function uses an interval to check the status of the membership and resolve a promise with the result.
      return new Promise<GetStripeProviderInfo_membershipSubscription_stripeProviderData | boolean>(
        // If the number of tries reaches 0 or the membership status is INVALID, the promise is rejected with a value of false.
        (resolve, reject) => {
          // The clearInterval function is called to stop the interval once the promise has been resolved or rejected.
          const interval: NodeJS.Timeout = setInterval(async () => {
            try {
              const { data: freshSubscriptionData } = await refetchSubscription();

              if (freshSubscriptionData?.membershipSubscription?.status === "ACTIVE") {
                resolve(true);
                clearInterval(interval);
              } else if (triesLeft <= 1) {
                reject(false);
                clearInterval(interval);
              }

              if (checkStripe === true) {
                try {
                  const { data: stripeProviderData } = await client.query({
                    query: GetStripeProviderInfoQuery,
                    fetchPolicy: "no-cache",
                  });
                  if (stripeProviderData?.membershipSubscription?.stripeProviderData?.status === "incomplete") {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                    resolve(stripeProviderData?.membershipSubscription?.stripeProviderData!);
                    clearInterval(interval);
                  }
                } catch (error) {}
              }

              if (
                triesLeft <= pollingTries - 130 &&
                freshSubscriptionData?.membershipSubscription?.status === "INVALID"
              ) {
                reject(false);
                clearInterval(interval);
              }
            } catch (error) {
              reject(false);
            }
            triesLeft--;
          }, pollingInterval);
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchSubscription]
  );

  // Check existing subscription
  // Tracks if the subscription has been checked. It is initialized to false
  const hasChecked = useRef(false);
  // useEffect performs the subscription check when subscriptionData or loginLoading change
  useEffect(() => {
    (async () => {
      // If the user is not logged in, the function returns early
      if (!userData) {
        return null;
      }

      // skipGetSubscription allows the GetSubscriptionInfo query to execute
      setSkipGetSubscription(false);
      // If the subscription has not been checked yet and the user is not logging in, the subscription is checked
      if (hasChecked.current === false && !loginLoading && subscriptionData) {
        hasChecked.current = true;

        // If the subscription was purchased through BrainTree, the user is redirected to the update payment method page
        if (subscription?.provider === MembershipSubscriptionProvider.BRAINTREE) {
          navigateToMyAccount(locale);
        }

        // If there is already an active subscription, the user is redirected to the welcome page
        else if (subscription?.status === "ACTIVE") {
          setPaymentLoading(false);
          window.location.href = getLoggedInHomeURL()!;
        }

        // If the subscription is processing, pollMembershipStatus checks the status and redirects the user if it becomes active
        else if (subscription?.status === MembershipSubscriptionStatus.NEW) {
          try {
            const result = await pollMembershipStatus();
            if (result === true) {
              setPaymentLoading(false);
              return (window.location.href = getWelcomeURL(planOrigin || selectedPlan)!);
            }
          } catch (e) {}
        }

        // If the subscription is non-active and was purchased through Stripe, paymentLoading is set to false
        else if (
          subscription?.provider === MembershipSubscriptionProvider.STRIPE &&
          (subscription?.status === MembershipSubscriptionStatus.INVALID ||
            subscription?.status === MembershipSubscriptionStatus.CANCELLED ||
            subscription?.status === MembershipSubscriptionStatus.EXPIRED)
        ) {
          return setPaymentLoading(false);
        }

        // If the subscription was purchased through an unsupported provider, the user is redirected to their account page
        else if (
          subscription &&
          subscription?.provider !== MembershipSubscriptionProvider.STRIPE &&
          subscription?.provider !== MembershipSubscriptionProvider.PAYPAL
        ) {
          navigateToMyAccount(locale);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData]);

  // isReady is a boolean indicating if payment processing is ready to proceed based on paymentLoading, elapsed time, and subscription status
  const isReady =
    (paymentLoading === "false" ||
      paymentLoading === "null" ||
      new Date().getTime() / 1000 - (paymentLoading as unknown as number) >= 900) &&
    subscription?.status !== "ACTIVE";

  // An event listener for 'beforeunload' is set up to prevent the window from closing if the subscription is not yet active and its status is 'NEW'.
  useEffect(() => {
    const handleTabClose = (event: BeforeUnloadEvent) => {
      if (!subscription) {
        event.preventDefault();
        return (event.returnValue = "Are you sure you want to exit?");
      }
    };
    window.addEventListener("beforeunload", handleTabClose);
    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [subscription]);

  // The subscription is refetched when the user logs in
  useEffect(() => {
    if (isLoggedIn) {
      refetchSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <Elements options={{ locale: locale }} stripe={stripePromise}>
        <PaymentContext.Provider
          value={{
            user: userData,
            selectedPlan: selectedPlan,
            setSelectedPlan: setSelectedPlan,
            selectedMethod: selectedMethod,
            setSelectedMethod: setSelectedMethod,
            isNew: isNew,
            isReady: isReady,
            paymentError: paymentError,
            setPaymentError: setPaymentError,
            paymentLoading: paymentLoading,
            setPaymentLoading: setPaymentLoading,
            refetchSubscription: refetchSubscription as () => Promise<ApolloQueryResult<GetSubscriptionInfo>>,
            pollMembershipStatus: pollMembershipStatus as () => Promise<
              boolean | GetStripeProviderInfo_membershipSubscription_stripeProviderData
            >,
            subscription: subscription,
          }}
        >
          {props.children}
        </PaymentContext.Provider>
      </Elements>
    </>
  );
};
